import { Box, Paper, Typography } from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { Dropdown } from '@commons/utils/styledLibraryComponents';

import { CHANNEL_LABELS, COLORS } from './utils/config';
import { formatNumberWithCurrency } from '@commons/DisplayNumber';
import { METRIC_TYPE } from '../common/utils';

const ChannelPieChart = ({
  isLoading,
  isPercentage,
  selectedStores,
  selectedMetric,
  categories,
  tableData,
  initialSelectedStores,
  title,
  currency,
}) => {
  const [selectedStoresForChart, setSelectedStoresForChart] = useState(selectedStores);
  const [chartData, setChartData] = useState([]);

  // Update selectedStoresForChart when initialSelectedStores changes
  useEffect(() => {
    if (initialSelectedStores && initialSelectedStores.length > 0) {
      setSelectedStoresForChart(initialSelectedStores);
    }
  }, [initialSelectedStores]);

  // Update chart data when stores or display format changes
  useEffect(() => {
    if (!tableData || !selectedStoresForChart || selectedStoresForChart.length === 0) {
      setChartData([]);
      return;
    }

    // Filter table data for selected stores
    const selectedStoreIds = selectedStoresForChart.map((store) => store.id);
    const filteredData = tableData.filter((row) => selectedStoreIds.includes(row.id));

    const aggregated = categories.reduce((acc, category) => {
      acc[category] = filteredData.reduce((sum, row) => sum + row[category]?.current, null);
      return acc;
    }, {});

    // If percentage mode, calculate percentages
    if (isPercentage) {
      const total = Object.values(aggregated).reduce((acc, curr) => acc + curr, 0);

      categories.forEach((category) => {
        aggregated[category] /= total ? total / 100 : 1;
      });
    }

    setChartData(
      Object.entries(aggregated)
        .map(([category, value]) => ({
          name: category,
          value,
        }))
        .filter(({ value }) => value),
    );
  }, [selectedStoresForChart, tableData, isPercentage]);

  // Custom label component for pie chart
  const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, value, name, fill }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const formattedValue = formatNumberWithCurrency({
      number: value,
      currency: currency,
      defaultCurrency: currency,
      displayCurrencyCode: selectedMetric.type === METRIC_TYPE.CURRENCY && !isPercentage,
      withoutDecimals: !isPercentage,
    });

    return (
      <text
        dominantBaseline="central"
        fill={fill}
        fontWeight="bold"
        textAnchor={x > cx ? 'start' : 'end'}
        x={x}
        y={y}
      >
        {`${CHANNEL_LABELS()[name]}: ${formattedValue} ${isPercentage ? '%' : ''}`}
      </text>
    );
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, height: 500, border: '1px solid #E0E0E0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
          <DeepsightComponentLoader />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: 500, border: '1px solid #E0E0E0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        <Dropdown
          customStyle={{ width: '300px' }}
          isRequired={true}
          isUniqueSelection={false}
          items={selectedStores}
          label={i18next.t('GENERAL.STORES')}
          searchPlaceholder={i18next.t('GENERAL.SEARCH')}
          selectedItems={selectedStoresForChart}
          onSelectionChange={setSelectedStoresForChart}
        />
      </Box>

      {chartData && chartData.length > 0 ? (
        <ResponsiveContainer height={300} width="100%">
          <PieChart>
            <Pie
              cx="50%"
              cy="50%"
              data={chartData}
              isAnimationActive={true}
              label={renderCustomLabel}
              labelLine={false}
              outerRadius={100}
            >
              {chartData.map((entry, index) => (
                <Cell fill={COLORS[entry.name]} key={`cell-${index}`} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography color="textSecondary" variant="body2">
            {i18next.t('GENERAL.NO_DATA_TO_DISPLAY')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
});

export default connect(mapStateToProps, null)(ChannelPieChart);
